import type { ProjectView } from '@/types/current-project';
import { Combobox } from '@components/ui';

interface ProjectsSearchBarProps {
  projects: ProjectView[];
  selectedProjectId: string;
  searchQuery: string;
  onProjectChange: (id: string) => void;
  onSearchChange: (value: string) => void;
  searchLabel?: string;
}

export function ProjectsSearchBar({
  projects,
  selectedProjectId,
  searchQuery,
  onProjectChange,
  onSearchChange,
  searchLabel = 'Search payments',
}: ProjectsSearchBarProps) {
  const activeProjects = projects;
  return (
    <div className="flex flex-col items-center justify-between gap-[8px] xl:flex-row">
      {projects.length > 1 && (
        <div className="flex w-full items-start gap-2 overflow-x-auto pb-2 xl:w-auto xl:pb-0">
          {activeProjects.slice(0, 3).map((proj) => (
            <button
              type="button"
              key={proj.projectId}
              aria-pressed={selectedProjectId === proj.projectId}
              onClick={() => onProjectChange(proj.projectId)}
              className={`flex h-[44px] shrink-0 items-center gap-[--spacing-5] rounded-full border pl-[--spacing-8] pr-[--spacing-6] transition-all ${
                selectedProjectId === proj.projectId
                  ? 'border-transparent bg-[--state-brand-active] text-[--text-light-primary]'
                  : 'border-[--base-border] bg-[--base-surface-2] text-[--text-dark-primary] hover:border-[--state-brand-active]'
              } shadow-elevation-sm`}
            >
              <span className="whitespace-nowrap text-base font-medium">
                {proj.title}
              </span>
            </button>
          ))}

          <Combobox
            aria-label="Select project"
            options={activeProjects.map((p) => ({
              value: p.projectId,
              label: p.title,
            }))}
            value={selectedProjectId}
            onChange={(e) => onProjectChange(e.target.value)}
            variant="ghost"
          />
        </div>
      )}

      <div className="relative w-full shrink-0 xl:ml-auto xl:w-[264px]">
        <i
          aria-hidden="true"
          className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-lg text-[rgba(255,255,255,0.7)]"
        />
        <input
          type="search"
          aria-label={searchLabel}
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-11 w-full rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] pl-12 pr-4 text-base font-medium text-[var(--text-dark-primary,#f5f5f5)] transition-all placeholder:text-[rgba(255,255,255,0.7)] focus:outline-none focus:ring-1 focus:ring-[var(--state-brand-active,#36d399)]"
        />
      </div>
    </div>
  );
}
